import React, { useEffect, useMemo, useState } from "react";
import api from "../api/client";
import { useAuth } from "../context/AuthContext";
import { useCustomer } from "../context/CustomerContext";
import M365LicenseModal from "../components/M365LicenseModal";
import OnboardUserModal from "../components/OnboardUserModal";
import OffboardUserModal from "../components/OffboardUserModal";
import { useSort, sortRows, SortableTh } from "../components/SortableTh";
import { useVisibleColumnCount, Breakpoint } from "../hooks/useVisibleColumns";
import RowActions from "../components/RowActions";
import { skuDisplay } from "../utils/m365";
import { M365Subscription, M365User } from "../types";
import ActionButton from "../components/ActionButton";
import { usePublishScreenData } from "../utils/aiScreenContext";
import { backdropClose } from "../components/ModalBackdrop";

/**
 * Hybrid Identity Phase 3 — manage Entra (cloud) users. Actions route
 * through Microsoft Graph (Phase 2 endpoints). Hybrid users (synced from
 * on-prem AD) are steered to AD Users for disable / password reset, since
 * AD is authoritative there; the UI offers a "force cloud action" override.
 */
export default function EntraUsers({ embedded = false }: { embedded?: boolean }) {
  const { user } = useAuth();
  const canAct = user?.role === "admin" || user?.role === "technician";
  const isAdmin = user?.role === "admin";
  const [licenseUser, setLicenseUser] = useState<M365User | null>(null);
  const [profileUser, setProfileUser] = useState<M365User | null>(null);

  // Scoped solely by the global customer switcher (no local picker).
  const { currentCustomerId, customers } = useCustomer();
  const customerId = currentCustomerId;
  const [users, setUsers] = useState<M365User[]>([]);
  const [subs, setSubs] = useState<M365Subscription[]>([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "enabled" | "disabled">("all");
  const { sort, toggle } = useSort("name");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [syncMsg, setSyncMsg] = useState("");

  const [onboardOpen, setOnboardOpen] = useState(false);
  const [offboardUser, setOffboardUser] = useState<M365User | null>(null);
  const [resetTarget, setResetTarget] = useState<M365User | null>(null);
  const [passwordResult, setPasswordResult] = useState<{ upn: string; password: string } | null>(
    null,
  );
  // Pending hybrid-blocked action awaiting force confirmation.
  const [forcePrompt, setForcePrompt] = useState<{
    u: M365User;
    action: "disable" | "reset-password";
    message: string;
  } | null>(null);

  const loadUsers = async () => {
    setLoading(true);
    setError("");
    try {
      // No customer selected means "All customers", which the endpoint already
      // supports by omitting the filter - and which the M365 page's own tab
      // count and banner have always claimed this table was showing. It used
      // to bail out here instead, so the table read "no users for this
      // customer" directly beneath a tab reading "Users (10750)".
      const cfg = customerId != null ? { params: { customer_id: customerId } } : undefined;
      const [u, s] = await Promise.all([
        api.get("/api/v1/m365/users", cfg),
        api.get("/api/v1/m365/subscriptions", cfg),
      ]);
      setUsers(u.data);
      setSubs(s.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to load Entra users");
    } finally {
      setLoading(false);
    }
  };

  // Whose user is this? Only asked in "All customers" mode - with one
  // customer selected the answer is already in the header, and a column
  // repeating the same value 10,000 times is noise.
  const showCustomer = customerId == null;
  // Indexed rather than searched linearly, for the same reason as the SKU map
  // below: called once per row, and per COMPARISON while sorting.
  const customerNames = useMemo(() => {
    const m: Record<number, string> = {};
    customers.forEach((c) => (m[c.id] = c.name));
    return m;
  }, [customers]);
  const customerName = (id?: number | null) =>
    (id != null ? customerNames[id] : null) || "—";

  // SKU id -> friendly license name, from the tenant's subscriptions.
  // Built ONCE. It used to be rebuilt inside licenseNames, which is called
  // per row and — via the sort comparator — per comparison. Over one tenant
  // that was invisible; over "All customers" it is 58 map inserts times
  // ~144k comparisons, i.e. sorting by License would hang the tab.
  const skuNameById = useMemo(() => {
    const m: Record<string, string> = {};
    subs.forEach((s) => (m[s.sku_id] = s.sku_part_number));
    return m;
  }, [subs]);
  const licenseNames = (u: M365User): string[] =>
    (u.license_skus || []).map((sid) => skuDisplay(skuNameById[sid] || sid));

  useEffect(() => {
    loadUsers(); /* eslint-disable-next-line */
  }, [customerId]);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 5000);
  };

  const patchLocally = (id: number, patch: Partial<M365User>) =>
    setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, ...patch } : u)));

  const triggerSync = async () => {
    setSyncMsg("");
    try {
      const r = await api.post("/api/v1/m365/sync");
      setSyncMsg(
        r.data.ok
          ? `Synced ${r.data.users ?? 0} user(s). Reloading…`
          : r.data.reason || "Sync did not run",
      );
      await loadUsers();
    } catch (err: any) {
      setSyncMsg(err.response?.data?.detail || "Sync failed");
    }
  };

  // Per-row busy flag: disables that user's action buttons while a Graph call
  // is in flight so the UI shows progress and can't queue duplicate requests.
  const [busyId, setBusyId] = useState<number | null>(null);

  const doDisable = async (u: M365User, force = false) => {
    setBusyId(u.id);
    try {
      await api.post(`/api/v1/m365/users/${u.id}/disable`, { allow_on_prem_synced: force });
      patchLocally(u.id, { account_enabled: false });
      showToast(`Disabled ${u.upn} in Entra.`);
    } catch (err: any) {
      if (err.response?.status === 409) {
        setForcePrompt({ u, action: "disable", message: err.response.data.detail });
        return;
      }
      alert(err.response?.data?.detail || "Failed to disable");
    } finally {
      setBusyId(null);
    }
  };

  const doEnable = async (u: M365User) => {
    setBusyId(u.id);
    try {
      await api.post(`/api/v1/m365/users/${u.id}/enable`);
      patchLocally(u.id, { account_enabled: true });
      showToast(`Re-enabled ${u.upn} in Entra.`);
    } catch (err: any) {
      alert(err.response?.data?.detail || "Failed to enable");
    } finally {
      setBusyId(null);
    }
  };

  const doRevoke = async (u: M365User) => {
    if (!window.confirm(`Revoke all active sign-in sessions for ${u.upn}?`)) return;
    setBusyId(u.id);
    try {
      await api.post(`/api/v1/m365/users/${u.id}/revoke-sessions`);
      showToast(`Revoked sessions for ${u.upn}. They'll need to sign in again.`);
    } catch (err: any) {
      alert(err.response?.data?.detail || "Failed to revoke sessions");
    } finally {
      setBusyId(null);
    }
  };

  const doResetMfa = async (u: M365User) => {
    if (
      !window.confirm(
        `Reset MFA for ${u.upn}?\n\nThis removes all their registered authentication ` +
          `methods (Authenticator, phone, FIDO2, etc.). They'll re-register at next ` +
          `sign-in. Their password is not changed.`,
      )
    )
      return;
    setBusyId(u.id);
    try {
      const r = await api.post(`/api/v1/m365/users/${u.id}/reset-mfa`);
      const n = r.data?.methods_deleted ?? 0;
      showToast(
        n > 0
          ? `Reset MFA for ${u.upn} — cleared ${n} method${n === 1 ? "" : "s"}. They'll re-register next sign-in.`
          : `No removable MFA methods were registered for ${u.upn}.`,
      );
      loadUsers();
    } catch (err: any) {
      alert(err.response?.data?.detail || "Failed to reset MFA");
    } finally {
      setBusyId(null);
    }
  };

  const doReset = async (u: M365User, force = false) => {
    setBusyId(u.id);
    try {
      const r = await api.post(`/api/v1/m365/users/${u.id}/reset-password`, {
        force_change: true,
        allow_on_prem_synced: force,
      });
      if (r.data.initial_password) {
        setPasswordResult({ upn: u.upn, password: r.data.initial_password });
      }
      setResetTarget(null);
      showToast(`Password reset for ${u.upn}.`);
    } catch (err: any) {
      if (err.response?.status === 409) {
        setResetTarget(null);
        setForcePrompt({ u, action: "reset-password", message: err.response.data.detail });
        return;
      }
      alert(err.response?.data?.detail || "Failed to reset password");
    } finally {
      setBusyId(null);
    }
  };

  const runForce = () => {
    if (!forcePrompt) return;
    const { u, action } = forcePrompt;
    setForcePrompt(null);
    if (action === "disable") doDisable(u, true);
    else doReset(u, true);
  };

  // How many rows are actually put in the DOM.
  //
  // This is the fix for "a small change like Revoke Sessions hangs the app to
  // Not Responding for ~2 min, then recovers; happens on every change". The
  // server was never the problem — measured, the user list returns in 0.33s.
  // The page rendered EVERY user, and one tenant here has 5,723 of them, each
  // row carrying license chips, a title attribute and five action buttons. Any
  // state change — including the one after a successful action — re-rendered
  // all 5,723, which is minutes of blocked main thread in the browser.
  //
  // Paginating caps the DOM at PAGE_SIZE rows no matter how large the tenant
  // is, so the cost of a re-render stops scaling with the customer.
  const PAGE_SIZE = 100;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const shown = useMemo(() => {
    const term = search.toLowerCase();
    return users.filter((u) => {
      const matchSearch =
        !term ||
        u.upn.toLowerCase().includes(term) ||
        (u.display_name || "").toLowerCase().includes(term);
      const matchStatus =
        statusFilter === "all" ||
        (statusFilter === "enabled" ? u.account_enabled : !u.account_enabled);
      return matchSearch && matchStatus;
    });
  }, [users, search, statusFilter]);

  useEffect(() => {
    // A narrowed search should start at the top of its own result set, not keep
    // however many rows the previous one had expanded to.
    setVisibleCount(PAGE_SIZE);
  }, [search, statusFilter, customerId]);

  usePublishScreenData(
    "Microsoft 365 users",
    shown.map((u: any) => ({
      upn: u.upn,
      name: u.display_name,
      enabled: u.account_enabled,
      synced_from_ad: u.on_premises_sync_enabled,
      licences: u.license_skus,
    })),
  );

  const sorted = sortRows(shown, sort, (u, key) => {
    switch (key) {
      case "upn":
        return (u.upn || "").toLowerCase();
      case "source":
        return u.on_premises_sync_enabled ? "hybrid" : "cloud";
      case "mfa":
        return u.is_mfa_capable ? 1 : 0;
      case "customer":
        return customerName(u.customer_id).toLowerCase();
      case "license":
        return licenseNames(u).join(", ").toLowerCase();
      case "status":
        return u.account_enabled ? 1 : 0;
      default:
        return (u.display_name || "").toLowerCase();
    }
  });

  // Only this slice reaches the DOM. Sorting still runs over the whole filtered
  // set, so paging never changes WHICH rows are the top ones.
  const page = sorted.slice(0, visibleCount);
  const hiddenCount = sorted.length - page.length;
  // Both dynamic (Customer only appears in "All customers") and
  // breakpoint-dependent. colSpan is an ATTRIBUTE, so no media query reaches
  // it - a hardcoded one holds the table at full width even with the spanned
  // columns hidden, which is how the equivalent fix on Devices silently did
  // nothing. Order MUST mirror the SortableTh list.
  const ENTRA_COLUMNS: Breakpoint[] = [
    "base", // Name
    ...(showCustomer ? (["md"] as Breakpoint[]) : []),
    "md",   // UPN
    "lg",   // Source
    "base", // MFA
    "lg",   // License
    "base", // Status
    "base", // Actions
  ];
  const COLS = useVisibleColumnCount(ENTRA_COLUMNS);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        {!embedded && <h1 className="text-2xl font-bold text-gray-900">Entra Users</h1>}
        <div className={`flex items-center gap-2 ${embedded ? "ml-auto" : ""}`}>
          {!customerId && !embedded && (
            <span className="text-sm text-gray-500">
              All customers &mdash; pick one in the top bar to onboard or manage users
            </span>
          )}
          {canAct && customerId && (
            <button
              onClick={() => setOnboardOpen(true)}
              className="bg-blue-600 text-white text-sm px-3 py-2 rounded-lg hover:bg-blue-700"
            >
              + Onboard user
            </button>
          )}
          {isAdmin && !embedded && (
            <ActionButton onAction={() => triggerSync()} variant="raw" className="border text-sm px-3 py-2 rounded-lg hover:bg-gray-50" pendingLabel="Syncing…">
              Sync now
            </ActionButton>
          )}
        </div>
      </div>

      {syncMsg && <div className="mb-3 text-xs text-blue-600">{syncMsg}</div>}
      {error && <div className="mb-3 text-sm text-red-500">{error}</div>}
      {toast && (
        <div className="mb-3 text-sm bg-blue-50 text-blue-800 border border-blue-200 px-4 py-2 rounded-lg flex items-center gap-2">
          <span>✓</span>
          {toast}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
        <div className="px-4 py-3 border-b dark:border-gray-700 flex items-center gap-2">
          <input
            placeholder="Search by UPN or name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
          >
            <option value="all">All</option>
            <option value="enabled">Enabled</option>
            <option value="disabled">Disabled</option>
          </select>
        </div>

        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700">
            <tr>
              <SortableTh label="Name" sortKey="name" sort={sort} onSort={toggle} />
              {showCustomer && (
                <SortableTh label="Customer" sortKey="customer" sort={sort} onSort={toggle} hide="hidden md:table-cell" />
              )}
              <SortableTh label="UPN" sortKey="upn" sort={sort} onSort={toggle} hide="hidden md:table-cell" />
              <SortableTh label="Source" sortKey="source" sort={sort} onSort={toggle} hide="hidden lg:table-cell" />
              <SortableTh label="MFA" sortKey="mfa" sort={sort} onSort={toggle} />
              <SortableTh label="License" sortKey="license" sort={sort} onSort={toggle} hide="hidden lg:table-cell" />
              <SortableTh label="Status" sortKey="status" sort={sort} onSort={toggle} />
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {loading && (
              <tr>
                <td colSpan={COLS} className="px-4 py-6 text-center text-gray-400 dark:text-gray-500">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && sorted.length === 0 && (
              <tr>
                <td colSpan={COLS} className="px-4 py-6 text-center text-gray-400 dark:text-gray-500">
                  {showCustomer
                    ? "No Entra users synced on any tenant yet. Connect a customer's M365 tenant and run a sync."
                    : "No Entra users synced for this customer. Configure the customer's M365 tenant and run a sync."}
                </td>
              </tr>
            )}
            {page.map((u) => {
              const lics = licenseNames(u);
              return (
              <tr
                key={u.id}
                className="hover:bg-gray-50 dark:hover:bg-gray-700/40 text-gray-800 dark:text-gray-200"
                title={`${u.display_name || u.upn}\n${u.upn}\nSource: ${u.on_premises_sync_enabled ? "Hybrid (on-prem AD)" : "Cloud"}\nMFA: ${u.is_mfa_capable ? "capable" : "not capable"}\nLicenses: ${lics.length ? lics.join(", ") : "none"}\nStatus: ${u.account_enabled ? "Enabled" : "Disabled"}${
                  u.admin_roles && u.admin_roles.length
                    ? `\nAdmin roles: ${u.admin_roles.join(", ")}`
                    : ""
                }`}
              >
                <td className="px-4 py-2 font-medium">
                  {u.display_name || "—"}
                  {/* A pill, not a column: the table already fills its
                      container to the pixel, and another column would push
                      Actions back off the edge.
                      Shown BEFORE the technician clicks anything, because
                      acting on an administrator is different - their password
                      cannot be reset through either Graph route without
                      Privileged Authentication Administrator, so "Reset
                      password" on a Global Admin fails after the fact unless
                      you knew going in. */}
                  {u.admin_roles && u.admin_roles.length > 0 && (
                    <span
                      title={`Microsoft 365 admin roles:\n${u.admin_roles.join("\n")}`}
                      // nowrap: "GLOBAL ADMIN" broke across two lines in the
                      // narrow Name column, which reads as a rendering fault
                      // rather than a badge.
                      className={`ml-2 align-middle whitespace-nowrap text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                        u.admin_roles.includes("Global Administrator")
                          ? "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
                          : "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
                      }`}
                    >
                      {u.admin_roles.includes("Global Administrator")
                        ? "GLOBAL ADMIN"
                        : u.admin_roles.length === 1
                          ? "ADMIN"
                          : `ADMIN ×${u.admin_roles.length}`}
                    </span>
                  )}
                </td>
                {showCustomer && (
                  <td className="px-4 py-2 text-gray-600 dark:text-gray-400 hidden md:table-cell">
                    {customerName(u.customer_id)}
                  </td>
                )}
                {/* Capped and truncated. Left to size itself, the UPN column
                    took 531px of a 1078px container and pushed the Actions
                    column off the right edge of its own overflow-x-auto box -
                    so the row's controls were only reachable by scrolling
                    sideways. The full value is in the row's title tooltip. */}
                <td
                  className="px-4 py-2 text-gray-600 dark:text-gray-400 max-w-[22rem] truncate hidden md:table-cell"
                  title={u.upn}
                >
                  {u.upn}
                </td>
                <td className="px-4 py-2 hidden lg:table-cell">
                  {u.on_premises_sync_enabled ? (
                    <span
                      className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300"
                      title="Synced from on-prem AD — disable/reset should be done in AD Users"
                    >
                      Hybrid
                    </span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300">
                      Cloud
                    </span>
                  )}
                </td>
                <td className="px-4 py-2">
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      u.is_mfa_capable
                        ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
                        : "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                    }`}
                  >
                    {u.is_mfa_capable ? "MFA" : "No MFA"}
                  </span>
                </td>
                <td className="px-4 py-2 hidden lg:table-cell">
                  {lics.length === 0 ? (
                    <span className="text-xs text-gray-400 dark:text-gray-500">No license</span>
                  ) : (
                    <div className="flex flex-wrap gap-1" title={lics.join(", ")}>
                      {lics.map((n, i) => (
                        <span
                          key={i}
                          className="text-xs bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 px-2 py-0.5 rounded-full"
                        >
                          {n}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td className="px-4 py-2">
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      u.account_enabled
                        ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
                        : "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
                    }`}
                  >
                    {u.account_enabled ? "Enabled" : "Disabled"}
                  </span>
                </td>
                <td className="px-4 py-2 text-right">
                  <RowActions
                    busy={busyId === u.id}
                    label={`Actions for ${u.display_name || u.upn}`}
                    actions={[
                      // Disable and Re-enable are the same slot: only one of
                      // them applies to a given account.
                      {
                        label: "Disable",
                        onClick: () => doDisable(u),
                        danger: true,
                        hidden: !canAct || !u.account_enabled,
                      },
                      {
                        label: "Re-enable",
                        onClick: () => doEnable(u),
                        hidden: !canAct || u.account_enabled,
                      },
                      { label: "Reset password", onClick: () => setResetTarget(u), hidden: !canAct },
                      { label: "Revoke sessions", onClick: () => doRevoke(u), hidden: !canAct },
                      {
                        label: "Reset MFA",
                        onClick: () => doResetMfa(u),
                        title:
                          "Remove all registered MFA methods so the user re-registers at next sign-in",
                        hidden: !canAct,
                      },
                      { label: "Licenses", onClick: () => setLicenseUser(u), hidden: !canAct },
                      { label: "Profile", onClick: () => setProfileUser(u), hidden: !canAct },
                      {
                        label: "Offboard",
                        onClick: () => setOffboardUser(u),
                        danger: true,
                        title: "Offboard across Microsoft 365 and on-prem AD",
                        hidden: !canAct,
                      },
                    ]}
                  />
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
        {/* Paging control. States the total so it is obvious the rest of the
            tenant is still there and searchable — a silently truncated list
            would be worse than a slow one. */}
        {hiddenCount > 0 && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-gray-100 dark:border-gray-700 text-sm">
            <span className="text-gray-500 dark:text-gray-400">
              Showing {page.length} of {sorted.length}
              {sorted.length !== users.length && <> (filtered from {users.length})</>}
            </span>
            <span className="flex items-center gap-2">
              <button
                onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
                className="border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-3 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40"
              >
                Show {Math.min(PAGE_SIZE, hiddenCount)} more
              </button>
              <button
                onClick={() => setVisibleCount(sorted.length)}
                title="Rendering every row in a large tenant can make the page briefly unresponsive"
                className="text-gray-500 dark:text-gray-400 hover:underline"
              >
                Show all {sorted.length}
              </button>
            </span>
          </div>
        )}
      </div>

      {licenseUser && (
        <M365LicenseModal
          user={licenseUser}
          skuDisplay={skuDisplay}
          onClose={() => setLicenseUser(null)}
          onChanged={loadUsers}
        />
      )}

      {profileUser && customerId && (
        <ProfileModal
          customerId={customerId}
          user={profileUser}
          onClose={() => setProfileUser(null)}
          onSaved={() => {
            setProfileUser(null);
            showToast(`Profile updated for ${profileUser.upn}.`);
            loadUsers();
          }}
        />
      )}

      {/* Onboard — combined AD + Microsoft 365 flow */}
      {onboardOpen && customerId && (
        <OnboardUserModal
          customerId={customerId}
          onClose={() => setOnboardOpen(false)}
          onRefresh={loadUsers}
        />
      )}

      {/* Offboard — combined AD + Microsoft 365 flow */}
      {offboardUser && customerId && (
        <OffboardUserModal
          customerId={customerId}
          upn={offboardUser.upn}
          displayName={offboardUser.display_name || offboardUser.upn}
          m365={{ id: offboardUser.id, on_prem: !!offboardUser.on_premises_sync_enabled }}
          onClose={() => setOffboardUser(null)}
          onDone={(summary) => {
            setOffboardUser(null);
            showToast(summary);
            loadUsers();
          }}
        />
      )}

      {/* Reset confirm */}
      {resetTarget && (
        <Modal title="Reset Entra password" onClose={() => setResetTarget(null)}>
          <p className="text-sm text-gray-600 mb-4">
            Reset the cloud password for <strong>{resetTarget.upn}</strong>. A strong password is
            generated and shown once; the user must change it at next sign-in.
          </p>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setResetTarget(null)}
              className="border text-sm px-4 py-2 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={() => doReset(resetTarget)}
              disabled={busyId === resetTarget.id}
              className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg disabled:opacity-50"
            >
              Reset
            </button>
          </div>
        </Modal>
      )}

      {/* Hybrid force confirm */}
      {forcePrompt && (
        <Modal title="On-prem synced user" onClose={() => setForcePrompt(null)}>
          <p className="text-sm text-gray-600 mb-4">{forcePrompt.message}</p>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setForcePrompt(null)}
              className="border text-sm px-4 py-2 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={runForce}
              className="bg-amber-600 text-white text-sm px-4 py-2 rounded-lg"
            >
              Force cloud action anyway
            </button>
          </div>
        </Modal>
      )}

      {/* Password reveal */}
      {passwordResult && (
        <Modal title="Password reset — shown once" onClose={() => setPasswordResult(null)}>
          <p className="text-sm text-gray-600 mb-2">
            New password for <strong>{passwordResult.upn}</strong>. Copy it now — it won't be shown
            again.
          </p>
          <div className="bg-gray-900 text-green-300 font-mono text-base rounded-lg px-3 py-3 select-all break-all mb-3">
            {passwordResult.password}
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => navigator.clipboard?.writeText(passwordResult.password)}
              className="border text-sm px-4 py-2 rounded-lg"
            >
              Copy
            </button>
            <button
              onClick={() => setPasswordResult(null)}
              className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg"
            >
              I've saved it
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}


/** Edit common directory attributes on an Entra user. Blank fields are left
 *  unchanged (not cleared). */
function ProfileModal({
  customerId,
  user,
  onClose,
  onSaved,
}: {
  customerId: number;
  user: M365User;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [jobTitle, setJobTitle] = useState("");
  const [department, setDepartment] = useState("");
  const [mobile, setMobile] = useState("");
  const [businessPhone, setBusinessPhone] = useState("");
  const [office, setOffice] = useState("");
  const [usage, setUsage] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post(`/api/v1/m365/${customerId}/users/${user.object_id}/profile`, {
        job_title: jobTitle || null,
        department: department || null,
        mobile_phone: mobile || null,
        business_phone: businessPhone || null,
        office_location: office || null,
        usage_location: usage || null,
      });
      onSaved();
    } catch (e: any) {
      setError(e.response?.data?.detail || "Update failed");
      setBusy(false);
    }
  };

  const field = (label: string, value: string, setter: (v: string) => void, placeholder = "") => (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <input
        value={value}
        onChange={(e) => setter(e.target.value)}
        placeholder={placeholder}
        className="w-full border rounded-lg px-3 py-2 text-sm"
      />
    </div>
  );

  return (
    <Modal title={`Edit profile — ${user.display_name || user.upn}`} onClose={onClose}>
      <p className="text-xs text-gray-500 mb-3">
        Leave a field blank to keep its current value.
      </p>
      <div className="space-y-3 mb-4">
        {field("Job title", jobTitle, setJobTitle, "Systems Engineer")}
        {field("Department", department, setDepartment, "IT")}
        <div className="grid grid-cols-2 gap-2">
          {field("Mobile phone", mobile, setMobile, "+1 555 0100")}
          {field("Business phone", businessPhone, setBusinessPhone, "+1 555 0101")}
        </div>
        {field("Office location", office, setOffice, "HQ — Floor 3")}
        {field("Usage location", usage, setUsage, "US")}
      </div>
      {error && <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded mb-3">{error}</p>}
      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="border text-sm px-4 py-2 rounded-lg">
          Cancel
        </button>
        <button
          onClick={submit}
          disabled={busy}
          className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg disabled:opacity-50"
        >
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </Modal>
  );
}

function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      {...backdropClose(onClose)}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold mb-4">{title}</h2>
        {children}
      </div>
    </div>
  );
}
